/**
 * Human Resources reports, built from the register, the records, attendance
 * and the cost the reader is allowed to see. One definition per report; the
 * reports screen shows each as a table, a chart, a CSV file or a print.
 */
import { costLine, eosb, eosbWage, grossPay, saudization, type HrCost, type HrEmployee, type HrRules } from "./hr";
import type { HrCan } from "./hrAccess";
import {
  attendanceSummary,
  comingUp,
  DATE_LABEL,
  LEAVE_BY,
  leaveDays,
  leaveType,
  LETTER_BY,
  serviceTo,
  sickLeave,
  vacationOf,
  type HrAttendanceRow,
  type HrRecord,
} from "./hrFile";

export type GroupBy = "department" | "trade" | "nationality" | "employment" | "gender" | "grade" | "costCentre";

export const GROUPS: { key: GroupBy; label: string }[] = [
  { key: "department", label: "Department" },
  { key: "trade", label: "Trade" },
  { key: "nationality", label: "Nationality" },
  { key: "employment", label: "Employment" },
  { key: "gender", label: "Gender" },
  { key: "grade", label: "Grade" },
  { key: "costCentre", label: "Cost center" },
];

export interface ReportDef {
  key: string;
  label: string;
  what: string;
  /** takes a group-by */
  grouped?: boolean;
  /** uses the period */
  period?: boolean;
  needs?: "pay" | "confidential";
}

export const REPORTS: ReportDef[] = [
  { key: "headcount", label: "Headcount", what: "How many people, grouped as you choose, with the Saudi share.", grouped: true },
  { key: "directory", label: "Staff directory", what: "Everyone with their department, title, phone and email." },
  { key: "attendance", label: "Attendance by person", what: "Days present, late, absent, on leave and sick in the period, and the attendance rate.", period: true },
  { key: "leave", label: "Leave taken", what: "Every approved leave in the period, by type.", period: true },
  { key: "sick", label: "Sick leave and pay tiers", what: "Sick days used in the last year and what is left at full, three-quarter and no pay (art. 117)." },
  { key: "balances", label: "Leave balances", what: "Annual leave earned, taken and left for each person, and what the balance is worth." },
  { key: "dates", label: "Birthdays, anniversaries and expiries", what: "What falls due in the next 90 days: birthdays, work anniversaries, ID, passport and contract ends." },
  { key: "saudization", label: "Saudization by department", what: "The Nitaqat share of each department." },
  { key: "letters", label: "Letters and requests", what: "Every letter issued or requested in the period, with its reference.", period: true },
  { key: "cost", label: "Manpower cost", what: "Monthly and yearly cost, grouped as you choose.", grouped: true, needs: "pay" },
  { key: "eosb", label: "End-of-service liability", what: "What each person would be owed on leaving today (art. 84).", needs: "pay" },
  { key: "reviews", label: "Performance reviews", what: "Each review with its rating and reviewer.", period: true, needs: "confidential" },
  { key: "discipline", label: "Disciplinary actions", what: "Each action, its type and whether it is closed.", period: true, needs: "confidential" },
];

export interface ReportColumn {
  key: string;
  label: string;
  num?: boolean;
}

export interface Report {
  columns: ReportColumn[];
  rows: Record<string, string | number | null>[];
  /** the column that names a person, so a row opens their file */
  personKey?: string;
  /** a column to chart against the first column */
  chart?: string;
  total?: Record<string, string | number | null>;
}

export interface ReportFilter {
  department?: string;
  employment?: "direct" | "outsourced" | "";
  saudi?: "saudi" | "non" | "";
  includeLeavers?: boolean;
  from: string;
  to: string;
}

export interface ReportInput {
  people: HrEmployee[];
  records: HrRecord[];
  attendance: HrAttendanceRow[];
  cost: HrCost | null;
  rules: HrRules;
  on: Date;
  can: HrCan;
}

export function filterPeople(people: HrEmployee[], f: ReportFilter): HrEmployee[] {
  return people.filter(
    (e) =>
      (f.includeLeavers || e.status !== "left") &&
      (!f.department || e.department === f.department) &&
      (!f.employment || e.employment === f.employment) &&
      (!f.saudi || (f.saudi === "saudi" ? e.saudi : !e.saudi)),
  );
}

const inPeriod = (day: string | null | undefined, f: ReportFilter) => !!day && day >= f.from && day <= f.to;
const r1 = (v: number) => Math.round(v * 10) / 10;
const val = (e: HrEmployee, g: GroupBy): string => {
  const v = (e as unknown as Record<string, unknown>)[g];
  return v == null || v === "" ? "—" : String(v);
};

export function buildReport(key: string, input: ReportInput, f: ReportFilter, groupBy: GroupBy = "department"): Report {
  const people = filterPeople(input.people, f);
  const ids = new Set(people.map((p) => p.id));

  const recs = input.records.filter((r) => ids.has(r.employeeId));

  switch (key) {
    case "headcount": {
      const groups = new Map<string, HrEmployee[]>();
      for (const e of people) groups.set(val(e, groupBy), [...(groups.get(val(e, groupBy)) ?? []), e]);
      const rows = [...groups.entries()]
        .map(([k, g]) => ({
          group: k,
          heads: g.length,
          saudi: g.filter((e) => e.saudi).length,
          direct: g.filter((e) => e.employment === "direct").length,
          share: g.length ? r1((g.filter((e) => e.saudi).length / g.length) * 100) : null,
        }))
        .sort((a, b) => b.heads - a.heads);
      return {
        columns: [
          { key: "group", label: GROUPS.find((g) => g.key === groupBy)?.label ?? "Group" },
          { key: "heads", label: "People", num: true },
          { key: "direct", label: "Direct", num: true },
          { key: "saudi", label: "Saudi", num: true },
          { key: "share", label: "Saudi %", num: true },
        ],
        rows,
        chart: "heads",
        total: { group: "Total", heads: people.length, direct: people.filter((e) => e.employment === "direct").length, saudi: people.filter((e) => e.saudi).length, share: people.length ? r1((people.filter((e) => e.saudi).length / people.length) * 100) : null },
      };
    }
    case "directory":
      return {
        columns: [
          { key: "no", label: "No." },
          { key: "name", label: "Name" },
          { key: "department", label: "Department" },
          { key: "title", label: "Title" },
          { key: "phone", label: "Phone" },
          { key: "email", label: "Email" },
        ],
        rows: people.map((e) => ({ employeeId: e.id, no: e.employeeNo, name: e.name, department: e.department, title: e.jobTitle, phone: e.phone ?? null, email: e.email ?? null })),
        personKey: "name",
      };
    case "attendance": {
      const rows = people.map((e) => {
        const mine = input.attendance.filter((a) => a.employeeId === e.id && a.day >= f.from && a.day <= f.to);
        const s = attendanceSummary(mine);
        return {
          employeeId: e.id,
          name: e.name,
          department: e.department,
          present: s.counts.present + s.counts.remote,
          late: s.counts.late,
          absent: s.counts.absent,
          leave: s.counts.leave,
          sick: s.counts.sick,
          lateMinutes: s.lateMinutes,
          rate: s.rate,
        };
      });
      return {
        columns: [
          { key: "name", label: "Name" },
          { key: "department", label: "Department" },
          { key: "present", label: "Present", num: true },
          { key: "late", label: "Late", num: true },
          { key: "absent", label: "Absent", num: true },
          { key: "leave", label: "On leave", num: true },
          { key: "sick", label: "Sick", num: true },
          { key: "lateMinutes", label: "Minutes late", num: true },
          { key: "rate", label: "Attendance %", num: true },
        ],
        rows: rows.sort((a, b) => (a.rate ?? 101) - (b.rate ?? 101)),
        personKey: "name",
        chart: "absent",
      };
    }
    case "leave": {
      const rows = recs
        .filter((r) => r.kind === "leave" && r.status === "approved" && inPeriod(r.startsOn, f))
        .map((r) => ({
          employeeId: r.employeeId,
          name: r.employeeName,
          department: r.department,
          type: LEAVE_BY.get(leaveType(r))?.label ?? leaveType(r),
          from: r.startsOn,
          to: r.endsOn,
          days: leaveDays(r),
        }));
      return {
        columns: [
          { key: "name", label: "Name" },
          { key: "department", label: "Department" },
          { key: "type", label: "Type" },
          { key: "from", label: "From" },
          { key: "to", label: "To" },
          { key: "days", label: "Days", num: true },
        ],
        rows,
        personKey: "name",
        total: { name: "Total", days: rows.reduce((s, r) => s + r.days, 0) },
      };
    }
    case "sick": {
      const rows = people
        .map((e) => {
          const s = sickLeave(recs.filter((r) => r.employeeId === e.id), input.on);
          return { employeeId: e.id, name: e.name, department: e.department, used: s.usedDays, full: s.fullPayLeft, three: s.threeQuarterLeft, unpaid: s.unpaidLeft, next: s.nextDayPay };
        })
        .filter((r) => r.used > 0)
        .sort((a, b) => b.used - a.used);
      return {
        columns: [
          { key: "name", label: "Name" },
          { key: "department", label: "Department" },
          { key: "used", label: "Sick days, 12 months", num: true },
          { key: "full", label: "Full pay left", num: true },
          { key: "three", label: "¾ pay left", num: true },
          { key: "unpaid", label: "Unpaid left", num: true },
          { key: "next", label: "Next sick day is paid" },
        ],
        rows,
        personKey: "name",
        chart: "used",
      };
    }
    case "balances": {
      const rows = people
        .filter((e) => e.employment === "direct")
        .map((e) => {
          const v = vacationOf(e, recs.filter((r) => r.employeeId === e.id), input.rules, input.on);
          return { employeeId: e.id, name: e.name, department: e.department, accrued: v.accrued, taken: v.taken, balance: v.balance, value: input.can.pay ? v.value : null };
        })
        .sort((a, b) => b.balance - a.balance);
      return {
        columns: [
          { key: "name", label: "Name" },
          { key: "department", label: "Department" },
          { key: "accrued", label: "Earned", num: true },
          { key: "taken", label: "Taken", num: true },
          { key: "balance", label: "Balance (days)", num: true },
          ...(input.can.pay ? [{ key: "value", label: "Worth (SAR)", num: true }] : []),
        ],
        rows,
        personKey: "name",
        chart: "balance",
        total: { name: "Total", balance: r1(rows.reduce((s, r) => s + r.balance, 0)), value: input.can.pay ? Math.round(rows.reduce((s, r) => s + (r.value ?? 0), 0)) : null },
      };
    }
    case "dates": {
      const rows = comingUp(people, input.on, 90).map((x) => ({
        employeeId: x.employeeId,
        name: x.name,
        what: DATE_LABEL[x.kind],
        date: x.date,
        inDays: x.daysAway,
        years: x.years,
      }));
      return {
        columns: [
          { key: "date", label: "Date" },
          { key: "inDays", label: "In days", num: true },
          { key: "name", label: "Name" },
          { key: "what", label: "What" },
          { key: "years", label: "Years", num: true },
        ],
        rows,
        personKey: "name",
      };
    }
    case "saudization": {
      const z = saudization(people, input.rules, input.on);
      return {
        columns: [
          { key: "group", label: "Department" },
          { key: "heads", label: "Direct people", num: true },
          { key: "counted", label: "Saudis counted", num: true },
          { key: "expats", label: "Non-Saudis", num: true },
          { key: "pct", label: "Saudization %", num: true },
        ],
        rows: z.byDepartment.map((x) => ({ group: x.key, heads: x.heads, counted: x.counted, expats: x.expats, pct: x.pct })),
        chart: "pct",
        total: { group: "Total", heads: z.heads, counted: z.counted, expats: z.expats, pct: z.pct },
      };
    }
    case "letters": {
      const rows = recs
        .filter((r) => r.kind === "letter" && inPeriod(r.occurredOn, f))
        .map((r) => ({
          employeeId: r.employeeId,
          name: r.employeeName,
          letter: LETTER_BY.get(String(r.data.letter_type ?? ""))?.label ?? r.title,
          status: r.status,
          reference: r.reference,
          date: r.occurredOn,
        }));
      return {
        columns: [
          { key: "date", label: "Date" },
          { key: "name", label: "Name" },
          { key: "letter", label: "Letter" },
          { key: "status", label: "Status" },
          { key: "reference", label: "Reference" },
        ],
        rows,
        personKey: "name",
      };
    }
    case "cost": {
      if (!input.can.pay) return { columns: [], rows: [] };
      const lines = people.map((e) => ({ e, l: costLine(e, input.rules, input.on) }));
      const groups = new Map<string, { heads: number; monthly: number }>();
      for (const { e, l } of lines) {
        const g = groups.get(val(e, groupBy)) ?? { heads: 0, monthly: 0 };
        g.heads++;
        g.monthly += l.monthlyCost;
        groups.set(val(e, groupBy), g);
      }
      const rows = [...groups.entries()]
        .map(([k, g]) => ({ group: k, heads: g.heads, monthly: Math.round(g.monthly), annual: Math.round(g.monthly * 12) }))
        .sort((a, b) => b.annual - a.annual);
      return {
        columns: [
          { key: "group", label: GROUPS.find((g) => g.key === groupBy)?.label ?? "Group" },
          { key: "heads", label: "People", num: true },
          { key: "monthly", label: "A month (SAR)", num: true },
          { key: "annual", label: "A year (SAR)", num: true },
        ],
        rows,
        chart: "annual",
        total: { group: "Total", heads: people.length, monthly: rows.reduce((s, r) => s + r.monthly, 0), annual: rows.reduce((s, r) => s + r.annual, 0) },
      };
    }
    case "eosb": {
      if (!input.can.pay) return { columns: [], rows: [] };
      const rows = people
        .filter((e) => e.employment === "direct" && grossPay(e) > 0)
        .map((e) => {
          const years = serviceTo(e, input.on);
          const a = eosb(eosbWage(e, input.rules), years, "termination", input.rules);
          const res = eosb(eosbWage(e, input.rules), years, "resignation", input.rules);
          return { employeeId: e.id, name: e.name, department: e.department, years: r1(years), wage: Math.round(a.wage), award: Math.round(a.amount), resign: Math.round(res.amount) };
        })
        .sort((a, b) => b.award - a.award);
      return {
        columns: [
          { key: "name", label: "Name" },
          { key: "department", label: "Department" },
          { key: "years", label: "Years", num: true },
          { key: "wage", label: "Wage (SAR)", num: true },
          { key: "award", label: "If ended by employer (SAR)", num: true },
          { key: "resign", label: "If they resign (SAR)", num: true },
        ],
        rows,
        personKey: "name",
        chart: "award",
        total: { name: "Total", award: rows.reduce((s, r) => s + r.award, 0), resign: rows.reduce((s, r) => s + r.resign, 0) },
      };
    }
    case "reviews":
    case "discipline": {
      if (!input.can.confidential) return { columns: [], rows: [] };
      const kind = key === "reviews" ? "review" : "discipline";
      const rows = recs
        .filter((r) => r.kind === kind && inPeriod(r.occurredOn, f))
        .map((r) => ({
          employeeId: r.employeeId,
          name: r.employeeName,
          department: r.department,
          date: r.occurredOn,
          title: r.title,
          detail: kind === "review" ? String(r.data.rating ?? "—") : String(r.data.action ?? "—"),
          status: r.status,
          by: r.createdByName,
        }));
      return {
        columns: [
          { key: "date", label: "Date" },
          { key: "name", label: "Name" },
          { key: "department", label: "Department" },
          { key: "title", label: "Title" },
          { key: "detail", label: kind === "review" ? "Rating" : "Action" },
          { key: "status", label: "Status" },
          { key: "by", label: "Recorded by" },
        ],
        rows,
        personKey: "name",
      };
    }
    default:
      return { columns: [], rows: [] };
  }
}

export function toCsv(r: Report, label: (s: string) => string = (s) => s): string {
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [r.columns.map((c) => esc(label(c.label))).join(",")];
  for (const row of r.rows) lines.push(r.columns.map((c) => esc(row[c.key])).join(","));
  if (r.total) lines.push(r.columns.map((c) => esc(r.total?.[c.key] ?? "")).join(","));
  return "﻿" + lines.join("\r\n");
}
